/**
 * Geführte Startfragen für Mikrobi.
 * Reihenfolge und Wortlaut können hier zentral geändert werden.
 * `prompt` ist der Text, der tatsächlich an Mikrobi gesendet wird
 * (falls nicht gesetzt, wird das Label gesendet).
 */

export interface MikrobiQuestion {
  id: string;
  de: string;
  en: string;
  promptDe?: string;
  promptEn?: string;
}

export interface MikrobiFaqCategory {
  id: string;
  de: string;
  en: string;
  questions: MikrobiQuestion[];
}

export const MIKROBI_FAQ: MikrobiFaqCategory[] = [
  {
    id: "beschwerden",
    de: "Beschwerden einordnen",
    en: "Understanding symptoms",
    questions: [
      {
        id: "schildern",
        de: "Ich möchte meine Beschwerden schildern",
        en: "I would like to describe my symptoms",
        promptDe:
          "Ich möchte dir meine Beschwerden und Symptome schildern. Bitte hilf mir, sie einzuordnen, und stelle mir dazu gezielte Rückfragen.",
        promptEn:
          "I would like to describe my symptoms. Please help me make sense of them and ask me targeted follow-up questions.",
      },
      {
        id: "reizdarm",
        de: "Reizdarm, Blähbauch und SIBO",
        en: "IBS, bloating and SIBO",
        promptDe:
          "Was steckt hinter Reizdarm, Blähbauch und SIBO, und wie wird in der PRAXIS IB damit gearbeitet?",
        promptEn: "What lies behind IBS, bloating and SIBO, and how does PRAXIS IB work with them?",
      },
      {
        id: "ced",
        de: "Morbus Crohn, Colitis, Divertikulitis",
        en: "Crohn's, colitis, diverticulitis",
        promptDe:
          "Wie kann bei chronisch-entzündlichen Darmerkrankungen wie Morbus Crohn, Colitis ulcerosa oder Divertikulitis begleitet werden?",
        promptEn:
          "How can inflammatory bowel conditions such as Crohn's disease, ulcerative colitis or diverticulitis be supported?",
      },
      {
        id: "blase",
        de: "Chronische Blasenentzündung und interstitielle Zystitis",
        en: "Recurrent cystitis and interstitial cystitis",
        promptDe:
          "Ich habe immer wiederkehrende Blasenentzündungen bzw. eine interstitielle Zystitis. Was kann dahinterstecken und wie wird in der PRAXIS IB vorgegangen?",
        promptEn:
          "I have recurrent bladder infections or interstitial cystitis. What may lie behind this and how does PRAXIS IB proceed?",
      },
      {
        id: "prostata",
        de: "Prostatitis und chronischer Beckenschmerz",
        en: "Prostatitis and chronic pelvic pain",
        promptDe:
          "Was ist bei chronischer Prostatitis und Beckenschmerzsyndrom (CPPS) mikrobiologisch und therapeutisch zu bedenken?",
        promptEn:
          "What should be considered microbiologically and therapeutically in chronic prostatitis and pelvic pain syndrome (CPPS)?",
      },
      {
        id: "hormone",
        de: "Endometriose, Zyklus und Hormone",
        en: "Endometriosis, cycle and hormones",
        promptDe:
          "Welche Zusammenhänge bestehen zwischen Darm, Östrogenstoffwechsel, Endometriose und Zyklusbeschwerden?",
        promptEn:
          "What connections exist between the gut, oestrogen metabolism, endometriosis and menstrual complaints?",
      },
      {
        id: "haut",
        de: "Haut: Neurodermitis, Akne, Rosazea, Psoriasis",
        en: "Skin: eczema, acne, rosacea, psoriasis",
        promptDe:
          "Wie hängen Hautbilder wie Neurodermitis, Akne, Rosazea oder Psoriasis mit Darm und Immunsystem zusammen?",
        promptEn:
          "How are skin conditions such as eczema, acne, rosacea or psoriasis linked to the gut and the immune system?",
      },
      {
        id: "unvertraeglichkeiten",
        de: "Histaminintoleranz und Unverträglichkeiten",
        en: "Histamine intolerance and food intolerances",
        promptDe:
          "Was ist bei Histaminintoleranz, Fructose- oder Laktoseunverträglichkeit sinnvoll abzuklären?",
        promptEn:
          "What is worth investigating in histamine intolerance, fructose or lactose intolerance?",
      },
      {
        id: "erschoepfung",
        de: "Erschöpfung, Fibromyalgie und rheumatische Beschwerden",
        en: "Exhaustion, fibromyalgia and rheumatic complaints",
        promptDe:
          "Ich bin dauerhaft erschöpft und habe Schmerzen (Fibromyalgie/Rheuma). Welche Zusammenhänge kommen infrage?",
        promptEn:
          "I am permanently exhausted and in pain (fibromyalgia/rheumatic complaints). Which connections come into question?",
      },
      {
        id: "infekte",
        de: "Wiederkehrende Infekte und Biofilm-Infektionen",
        en: "Recurrent infections and biofilm infections",
        promptDe:
          "Warum kehren Infekte immer wieder, und welche Rolle spielen Biofilme und Keimresistenzen?",
        promptEn:
          "Why do infections keep coming back, and what role do biofilms and antimicrobial resistance play?",
      },
    ],
  },
  {
    id: "arbeitsweise",
    de: "Wie in der PRAXIS IB gearbeitet wird",
    en: "How PRAXIS IB works",
    questions: [
      {
        id: "ansatz",
        de: "Mikrobiom im Zentrum – aber nie isoliert",
        en: "The microbiome at the centre – but never in isolation",
        promptDe:
          "Was bedeutet es, dass in der PRAXIS IB das Mikrobiom im Zentrum steht, aber nie isoliert betrachtet wird?",
        promptEn:
          "What does it mean that PRAXIS IB places the microbiome at the centre, yet never views it in isolation?",
      },
      {
        id: "schulmedizin",
        de: "Schulmedizin und Komplementärmedizin gemeinsam",
        en: "Conventional and complementary medicine together",
        promptDe:
          "Wie arbeiten Schulmedizin und Komplementärmedizin in der PRAXIS IB zusammen, auch bei Überweisungen von Ärztinnen und Ärzten?",
        promptEn:
          "How do conventional and complementary medicine work together at PRAXIS IB, including referrals from physicians?",
      },
      {
        id: "biochemie",
        de: "Biochemische Abläufe und Regelkreise",
        en: "Biochemical pathways and control loops",
        promptDe:
          "Welche biochemischen Abläufe und Regelkreise werden betrachtet – etwa Methylierung, Mitochondrien, Entgiftung, Entzündungsbilanz?",
        promptEn:
          "Which biochemical pathways and control loops are considered – methylation, mitochondria, detoxification, inflammatory balance?",
      },
      {
        id: "phyto",
        de: "Phytotherapie in Arzneimittelqualität",
        en: "Phytotherapy in pharmaceutical quality",
        promptDe:
          "Wie wird Phytotherapie in der PRAXIS IB eingesetzt und was bedeutet Arzneimittelqualität?",
        promptEn:
          "How is phytotherapy used at PRAXIS IB and what does pharmaceutical quality mean?",
      },
      {
        id: "mikronaehrstoffe",
        de: "Mikronährstoffe, Vitaminoide und Nukleotide",
        en: "Micronutrients, vitaminoids and nucleotides",
        promptDe:
          "Welche Rolle spielen Mikronährstoffe, Vitaminoide wie Coenzym Q10 oder Alpha-Liponsäure und Nukleotide?",
        promptEn:
          "What role do micronutrients, vitaminoids such as coenzyme Q10 or alpha-lipoic acid, and nucleotides play?",
      },
      {
        id: "antiinflammatorisch",
        de: "Antiinflammatorische Strategien und Immunmodulation",
        en: "Anti-inflammatory strategies and immune modulation",
        promptDe:
          "Wie wird mit stiller Entzündung und Immunmodulation gearbeitet, etwa mit Bakterienlysaten?",
        promptEn:
          "How is low-grade inflammation addressed and how does immune modulation work, for instance with bacterial lysates?",
      },
      {
        id: "phagen",
        de: "Bakteriophagen-Therapie",
        en: "Bacteriophage therapy",
        promptDe:
          "Was ist die Bakteriophagen-Therapie, für wen kommt sie infrage und wie läuft die Kooperation mit dem Eliava-Institut ab?",
        promptEn:
          "What is bacteriophage therapy, who is it suitable for, and how does the cooperation with the Eliava Institute work?",
      },
      {
        id: "pni",
        de: "Kinesiologie und Psychoneuroimmunologie",
        en: "Kinesiology and psychoneuroimmunology",
        promptDe:
          "Welche Rolle spielen Kinesiologie und Psychoneuroimmunologie in der Begleitung?",
        promptEn: "What role do kinesiology and psychoneuroimmunology play in the care provided?",
      },
      {
        id: "individuell",
        de: "Individueller Plan statt Schema F",
        en: "An individual plan instead of a standard protocol",
        promptDe:
          "Warum gibt es keine Standardprotokolle und wie entsteht ein individueller Therapieplan?",
        promptEn:
          "Why are there no standard protocols, and how is an individual treatment plan developed?",
      },
    ],
  },
  {
    id: "termin",
    de: "Termin & Ablauf",
    en: "Appointment & process",
    questions: [
      {
        id: "erstgespraech",
        de: "Was erwartet mich im Erstgespräch?",
        en: "What happens in the first consultation?",
      },
      {
        id: "dauer-kosten",
        de: "Wie lange dauert ein Termin und was kostet er?",
        en: "How long does an appointment take and what does it cost?",
      },
      {
        id: "vorort-zoom",
        de: "Praxis vor Ort (Zoom nach Vereinbarung) – was ist möglich?",
        en: "In practice (Zoom by arrangement) – what is possible?",
      },
      {
        id: "anzahl",
        de: "Wie viele Termine sind üblich?",
        en: "How many appointments are usual?",
      },
      {
        id: "befunde",
        de: "Welche Befunde soll ich mitbringen?",
        en: "Which findings should I bring along?",
      },
      {
        id: "buchen",
        de: "Wie buche ich einen Termin?",
        en: "How do I book an appointment?",
      },
    ],
  },
  {
    id: "diagnostik",
    de: "Diagnostik & Labor",
    en: "Diagnostics & laboratory",
    questions: [
      {
        id: "stuhl",
        de: "Was zeigt eine Stuhl- und Mikrobiomanalyse?",
        en: "What does a stool and microbiome analysis show?",
      },
      {
        id: "microgendx",
        de: "Was ist die MicroGenDX qPCR/NGS-Diagnostik?",
        en: "What is MicroGenDX qPCR/NGS diagnostics?",
      },
      {
        id: "atemtest",
        de: "Wann ist ein Atemtest (SIBO, Fructose, Laktose) sinnvoll?",
        en: "When is a breath test (SIBO, fructose, lactose) useful?",
      },
      {
        id: "blut",
        de: "Welche Blutwerte werden betrachtet?",
        en: "Which blood values are considered?",
      },
      {
        id: "kasse",
        de: "Was übernimmt die Krankenkasse?",
        en: "What does health insurance cover?",
      },
    ],
  },
  {
    id: "therapeutin",
    de: "Über die akad. zert. dipl. Komplementärtherapeutin Isabella Bartak",
    en: "About Isabella Bartak, certified complementary therapist",
    questions: [
      {
        id: "qualifikationen",
        de: "Welche Ausbildungen und Zertifizierungen liegen vor?",
        en: "Which qualifications and certifications are held?",
      },
      {
        id: "schwerpunkte",
        de: "Welche fachlichen Schwerpunkte gibt es?",
        en: "What are the professional focus areas?",
      },
      {
        id: "eliava",
        de: "Was bedeutet die Eliava-Zertifizierung?",
        en: "What does the Eliava certification mean?",
      },
      {
        id: "buch",
        de: "Worum geht es im Buch „Seelenbalance“?",
        en: "What is the book \u201eSeelenbalance\u201c about?",
      },
    ],
  },
  {
    id: "magazin",
    de: "Magazin & Seiten finden",
    en: "Magazine & finding pages",
    questions: [
      {
        id: "artikel",
        de: "Gibt es einen Magazinartikel zu meinem Thema?",
        en: "Is there a magazine article on my topic?",
        promptDe:
          "Ich suche einen Magazinartikel zu einem bestimmten Thema. Frage mich, welches Thema mich interessiert, und verlinke dann die passenden Artikel.",
        promptEn:
          "I am looking for a magazine article on a particular topic. Ask me which topic interests me and then link the matching articles.",
      },
      {
        id: "seite",
        de: "Welche Seite passt zu meinem Anliegen?",
        en: "Which page fits my concern?",
        promptDe:
          "Ich weiß nicht, welche Seite zu meinem Anliegen passt. Bitte hilf mir, mich auf der Website zurechtzufinden.",
        promptEn:
          "I do not know which page fits my concern. Please help me find my way around the website.",
      },
      {
        id: "neu",
        de: "Was gibt es Neues im Magazin?",
        en: "What is new in the magazine?",
      },
    ],
  },
];

export const questionPrompt = (q: MikrobiQuestion, lang: "de" | "en") =>
  lang === "en" ? (q.promptEn ?? q.en) : (q.promptDe ?? q.de);

export const questionLabel = (q: MikrobiQuestion, lang: "de" | "en") =>
  lang === "en" ? q.en : q.de;

export const categoryLabel = (c: MikrobiFaqCategory, lang: "de" | "en") =>
  lang === "en" ? c.en : c.de;
